import { useEffect, useRef, useState } from 'react';
import { getGeodata } from 'api/geodataApi';
import { getGrid } from 'api/gridApi';
import { getProject } from 'api/projectApi';
import { getSimulationSet } from 'api/simulationSetApi';
import { getSimulations } from 'api/simulationApi';
import { normalizeGeodataParams, type GeodataParams } from 'contexts/GeodataContext';
import { isWithinSwitzerland } from 'services/isWithinSwitzerland';
import { firstPolygon } from 'utils/multiPolygonHelper';
import {
  normalizeGridForSimulationView,
  normalizeProjectForMap,
  type SimulationGeodata,
  type SimulationGrid,
  type SimulationGridView,
  type SimulationProjectData,
} from './addSimulationShared';

type UseAddSimulationDataArgs = {
  currentProject: Record<string, unknown> | null | undefined;
  geodataDraft: {
    name?: string | null;
    description?: string | null;
    buildingsEnabled?: boolean;
    treesEnabled?: boolean;
    params?: Record<string, unknown>;
  } | null;
  geodataIdFromRoute: string | null | undefined;
  gridDraft: { id?: string | number | null; projectId?: string | number | null; params?: unknown } | null;
  gridIdOverride: string | null;
  projectIdOverride: string | null;
  simulationSetIdOverride: string | null;
  isProjectCreationFlow: boolean;
  isReadOnlyReview: boolean;
  resolvedReviewSimulationId: string | null | undefined;
  cloneSimulationData: (simulationData: Record<string, unknown>, options?: { appendClonedSuffix?: boolean }) => void;
  isCloningSimulation: boolean;
  setGeodataParams: (params: Record<string, unknown>) => void;
  setIsCloningSimulation: (value: boolean) => void;
};

const useAddSimulationData = ({
  currentProject,
  geodataDraft,
  geodataIdFromRoute,
  gridDraft,
  gridIdOverride,
  projectIdOverride,
  simulationSetIdOverride,
  isProjectCreationFlow,
  isReadOnlyReview,
  resolvedReviewSimulationId,
  cloneSimulationData,
  isCloningSimulation,
  setGeodataParams,
  setIsCloningSimulation,
}: UseAddSimulationDataArgs) => {
  const [geodataId, setGeodataId] = useState<string | null>(geodataIdFromRoute ?? null);
  const [geodata, setGeodata] = useState<SimulationGeodata | null>(null);
  const [grid, setGrid] = useState<SimulationGridView | null>(null);
  const [project, setProject] = useState<SimulationProjectData | null>(null);
  const [isTheAreaInSwitzerland, setIsTheAreaInSwitzerland] = useState(false);
  const isCloneSessionRef = useRef(isCloningSimulation);

  useEffect(() => {
    isCloneSessionRef.current = isCloningSimulation;
  }, [isCloningSimulation]);

  useEffect(() => {
    if (!isReadOnlyReview && !isCloneSessionRef.current) {
      setIsCloningSimulation(false);
    }

    return () => {
      setIsCloningSimulation(false);
    };
  }, [isReadOnlyReview, setIsCloningSimulation]);

  useEffect(() => {
    if (!isReadOnlyReview) {
      setGeodataId(geodataIdFromRoute ?? null);
      return;
    }
    if (!resolvedReviewSimulationId) return;

    let isCancelled = false;

    const loadReviewSimulationData = async () => {
      const simulationSetData = await getSimulationSet(String(resolvedReviewSimulationId));
      if (!simulationSetData || isCancelled) return;
      const subSimulations = await getSimulations(String(simulationSetData.id));
      if (isCancelled) return;

      cloneSimulationData(
        {
          ...simulationSetData,
          subSimulations,
        },
        { appendClonedSuffix: false }
      );
      setIsCloningSimulation(true);
      setGeodataId(simulationSetData.geodataId ?? null);
    };

    loadReviewSimulationData().catch((error) => {
      console.error('Error loading simulation set:', error);
    });

    return () => {
      isCancelled = true;
    };
  }, [cloneSimulationData, geodataIdFromRoute, isReadOnlyReview, resolvedReviewSimulationId, setIsCloningSimulation]);

  useEffect(() => {
    if (!simulationSetIdOverride || isReadOnlyReview) return;

    let isCancelled = false;
    const loadSimulationSet = async () => {
      const simulationSetData = await getSimulationSet(simulationSetIdOverride);
      const subSimulations = await getSimulations(simulationSetIdOverride);
      if (!isCancelled) {
        cloneSimulationData({ ...simulationSetData, subSimulations }, { appendClonedSuffix: false });
      }
    };
    loadSimulationSet().catch((error) => console.error('Error loading simulation set:', error));
    return () => {
      isCancelled = true;
    };
  }, [cloneSimulationData, isReadOnlyReview, simulationSetIdOverride]);

  useEffect(() => {
    if (!geodataId) return;

    let cancelled = false;

    const loadGeodata = async () => {
      try {
        const geodataData = await getGeodata(String(geodataId));
        if (cancelled || !geodataData) return;

        const normalizedParams = normalizeGeodataParams({
          ...(geodataData?.terrain ?? {}),
          ...(geodataData?.building ?? {}),
          ...(geodataData?.vegetation ?? {}),
        });

        const normalizedGeodata = {
          ...geodataData,
          params: normalizedParams,
        };

        setGeodata(normalizedGeodata);

        if (!isProjectCreationFlow && normalizedGeodata?.params) {
          setGeodataParams({
            ...normalizedGeodata.params,
            lengthMid: Number(normalizedGeodata.params.lengthMid ?? 500),
            resolutionFar: Number(normalizedGeodata.params.resolutionFar ?? 180),
          });
        }
      } catch (error) {
        console.error('Error loading geodata:', error);
      }
    };

    loadGeodata();

    return () => {
      cancelled = true;
    };
  }, [geodataId, isProjectCreationFlow, setGeodataParams]);

  useEffect(() => {
    if (!isProjectCreationFlow) return;

    setGeodata((previousGeodata) => {
      if (!previousGeodata) {
        return previousGeodata;
      }

      const previousParams = (previousGeodata.params ?? {}) as Partial<GeodataParams>;
      const draftParams = (geodataDraft?.params ?? {}) as Partial<GeodataParams>;

      return {
        ...previousGeodata,
        name: geodataDraft?.name || previousGeodata.name,
        description: geodataDraft?.description || previousGeodata.description,
        hasBuilding:
          geodataDraft?.buildingsEnabled ??
          Boolean(
            (previousGeodata as { hasBuilding?: unknown })?.hasBuilding ??
            (previousGeodata as { building?: unknown })?.building
          ),
        hasVegetation:
          geodataDraft?.treesEnabled ??
          Boolean(
            (previousGeodata as { hasVegetation?: unknown })?.hasVegetation ??
            (previousGeodata as { vegetation?: unknown })?.vegetation
          ),
        params: normalizeGeodataParams({
          ...previousParams,
          ...draftParams,
        }),
      };
    });
  }, [
    geodataDraft?.buildingsEnabled,
    geodataDraft?.description,
    geodataDraft?.name,
    geodataDraft?.params,
    geodataDraft?.treesEnabled,
    isProjectCreationFlow,
  ]);

  useEffect(() => {
    if (isProjectCreationFlow) {
      const localGridData = normalizeGridForSimulationView({
        ...(gridDraft as unknown as SimulationGrid),
        id: gridIdOverride ?? gridDraft?.id ?? null,
        projectId: projectIdOverride ?? gridDraft?.projectId ?? currentProject?.id ?? null,
        params: (gridDraft?.params ?? {}) as SimulationGrid['params'],
      } as SimulationGrid);

      setGrid(localGridData);

      const gridParamsRecord: Record<string, unknown> = localGridData.params ?? {};
      const analysisAreaCoordinates = Array.isArray(gridParamsRecord.analysisArea) ? gridParamsRecord.analysisArea : [];
      const analysisAreaType =
        typeof gridParamsRecord.analysisAreaType === 'string' ? gridParamsRecord.analysisAreaType : undefined;
      setIsTheAreaInSwitzerland(isWithinSwitzerland(analysisAreaCoordinates, analysisAreaType));
      return;
    }

    const resolvedGridId = geodata?.gridId;
    if (!resolvedGridId) return;

    let cancelled = false;

    const loadGrid = async () => {
      try {
        const rawGridData = await getGrid(String(resolvedGridId));
        if (cancelled || !rawGridData) return;

        const gridData = normalizeGridForSimulationView(rawGridData);
        setGrid(gridData);

        const gridParams: Record<string, unknown> = gridData.params ?? {};
        const analysisAreaCoordinates = firstPolygon<{ x: number; y: number }>(gridParams.analysisArea);
        const analysisAreaType =
          typeof gridParams.analysisAreaType === 'string' ? gridParams.analysisAreaType : undefined;
        setIsTheAreaInSwitzerland(isWithinSwitzerland(analysisAreaCoordinates, analysisAreaType));
      } catch (error) {
        console.error('Error loading grid:', error);
      }
    };

    loadGrid();

    return () => {
      cancelled = true;
    };
  }, [currentProject?.id, geodata?.gridId, gridDraft, gridIdOverride, isProjectCreationFlow, projectIdOverride]);

  useEffect(() => {
    if (isProjectCreationFlow) {
      setProject(currentProject ? normalizeProjectForMap(currentProject) : null);
      return;
    }

    const projectId = grid?.projectId;
    if (!projectId) {
      setProject(null);
      return;
    }

    let cancelled = false;

    const loadProject = async () => {
      try {
        const projectData = await getProject(String(projectId));
        if (cancelled || !projectData) return;

        setProject(normalizeProjectForMap(projectData as Record<string, unknown>));
      } catch (error) {
        console.error('Error loading project:', error);
      }
    };

    loadProject();

    return () => {
      cancelled = true;
    };
  }, [currentProject, grid?.projectId, isProjectCreationFlow]);

  return {
    geodataId,
    geodata,
    grid,
    project,
    isCloneSessionRef,
    isTheAreaInSwitzerland,
    setGeodata,
  };
};

export default useAddSimulationData;
